import { Fragment, useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface SettlementSummary {
  id: number; settlementId: string;
  settlementStart: string; settlementEnd: string; depositDate: string;
  totalAmount: number; totalAmountUsd: number; currency: string; originalCurrency: string;
  itemCount: number; salesAmount: number; refundAmount: number;
  feeAmount: number; promotionAmount: number; reimbursementAmount: number; otherAmount: number;
  expectedRevenue: number | null; expectedFees: number | null;
  revenueDiscrepancy: number | null; feeDiscrepancy: number | null;
  reconciledAt: string | null;
}

interface KPIs { totalDeposited: number; settlementCount: number; avgAmount: number; discrepancies: number }
interface SettlementsResponse { settlements: SettlementSummary[]; total: number; kpis: KPIs }

interface Diagnostic { reason: string; amount: number; explanation: string }
interface DiagnosticInfo { diagnostics: Diagnostic[]; totalDiscrepancy: number; explainedAmount: number; unresolvedAmount: number }
interface Account { id: string; name: string; is_active?: number }

// P&L types (merged from FinancePnlReport)
interface PnlKpis {
  totalRevenue: number; productSales: number; shippingCredits: number; giftWrapCredits: number;
  productSalesTax: number; promotionalRebates: number; sellingFees: number; fbaFees: number;
  otherFees: number; totalFees: number; marketplaceWithheldTax: number; netProceeds: number;
  transactionCount: number;
}
interface DailyPnl { date: string; product_sales: number; selling_fees: number; fba_fees: number; promotional_rebates: number; total: number }
interface PnlResponse { kpis: PnlKpis; daily: DailyPnl[] }
interface Transaction { id: number; settlementId: string; orderId: string; sku: string; amountType: string; amountDescription: string; amount: number }
interface TransactionsResponse { transactions: Transaction[]; total: number }

const CURRENCY_TO_MARKET: Record<string, string> = { USD: 'US', CAD: 'CA', GBP: 'UK', MXN: 'MX', EUR: 'EU' };
const MARKET_CURRENCIES: Record<string, string> = { All: '', US: 'USD', CA: 'CAD', UK: 'GBP', MX: 'MXN', EU: 'EUR' };

// ── Component ────────────────────────────────────────────────────────────────

export function FinanceSettlements() {
  const { displayName, parentNickname } = useNicknames();
  const [data, setData] = useState<SettlementsResponse | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [reconcilingId, setReconcilingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [diagnosticsData, setDiagnosticsData] = useState<Record<string, DiagnosticInfo | null>>({});

  // Page tabs
  const [pageTab, setPageTab] = useState<'settlements' | 'breakdown' | 'transactions'>('settlements');

  // P&L state
  const [pnl, setPnl] = useState<PnlResponse | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txOffset, setTxOffset] = useState(0);
  const [txLoading, setTxLoading] = useState(false);

  // Date range for P&L
  const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));

  // Filters
  const [marketFilter, setMarketFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState<'all' | 'reconciled' | 'unreconciled' | 'mismatch'>('all');
  const [sortBy, setSortBy] = useState<'deposit' | 'amount' | 'period'>('deposit');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');

  useEffect(() => {
    api.get<Account[]>('/accounts').then((accts) => {
      const active = accts.filter(a => (a as any).is_active !== 0);
      setAccounts(active);
      if (active.length === 0) setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (accounts.length === 0) return;
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  function loadData() {
    setLoading(true); setError('');
    Promise.all(
      accounts.map(a =>
        api.get<SettlementsResponse>(`/finance/settlements/${a.id}?limit=200`)
          .catch(() => ({ settlements: [], total: 0, kpis: { totalDeposited: 0, settlementCount: 0, avgAmount: 0, discrepancies: 0 } }))
      )
    ).then(results => {
      const allSettlements = results.flatMap(r => r.settlements);
      allSettlements.sort((a, b) => (b.depositDate ?? '').localeCompare(a.depositDate ?? ''));
      const mergedKpis: KPIs = { totalDeposited: 0, settlementCount: 0, avgAmount: 0, discrepancies: 0 };
      for (const r of results) {
        mergedKpis.totalDeposited += r.kpis.totalDeposited;
        mergedKpis.settlementCount += r.kpis.settlementCount;
        mergedKpis.discrepancies += r.kpis.discrepancies;
      }
      mergedKpis.avgAmount = mergedKpis.settlementCount > 0 ? mergedKpis.totalDeposited / mergedKpis.settlementCount : 0;
      setData({ settlements: allSettlements, total: allSettlements.length, kpis: mergedKpis });
    }).catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }

  // Load P&L breakdown + transactions
  useEffect(() => {
    if (accounts.length === 0 || pageTab === 'settlements') return;
    if (pageTab === 'breakdown') {
      Promise.all(accounts.map(a =>
        api.get<PnlResponse>(`/finance/pnl/${a.id}?start=${startDate}&end=${endDate}`).catch(() => null)
      )).then(results => {
        const valid = results.filter((r): r is PnlResponse => r !== null);
        if (valid.length === 0) { setPnl(null); return; }
        const merged = valid[0];
        for (let i = 1; i < valid.length; i++) {
          const r = valid[i];
          for (const k of Object.keys(merged.kpis) as (keyof PnlKpis)[]) {
            (merged.kpis[k] as number) += r.kpis[k] as number;
          }
          merged.daily.push(...r.daily);
        }
        // Re-aggregate daily by date
        const dayMap = new Map<string, DailyPnl>();
        for (const d of merged.daily) {
          const ex = dayMap.get(d.date);
          if (ex) { ex.product_sales += d.product_sales; ex.selling_fees += d.selling_fees; ex.fba_fees += d.fba_fees; ex.promotional_rebates += d.promotional_rebates; ex.total += d.total; }
          else dayMap.set(d.date, { ...d });
        }
        merged.daily = [...dayMap.values()].sort((a, b) => b.date.localeCompare(a.date));
        setPnl(merged);
      });
    }
    if (pageTab === 'transactions' && accounts.length > 0) {
      setTxLoading(true);
      api.get<TransactionsResponse>(`/finance/pnl/${accounts[0].id}/transactions?start=${startDate}&end=${endDate}&limit=100&offset=${txOffset}`)
        .then(r => { setTransactions(r.transactions); setTxTotal(r.total); })
        .catch(() => {})
        .finally(() => setTxLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, pageTab, startDate, endDate, txOffset]);

  async function handleImport() {
    setImporting(true); setImportMsg('');
    try {
      const msgs: string[] = [];
      for (const a of accounts) {
        const result = await api.post<{ message: string }>(`/finance/settlements/${a.id}/import`);
        msgs.push(`${a.name}: ${result.message}`);
      }
      setImportMsg(msgs.join(' | '));
      loadData();
    } catch (err) { setImportMsg(err instanceof Error ? err.message : 'Import failed'); }
    finally { setImporting(false); }
  }

  async function handleReconcile(settlementId: string) {
    setReconcilingId(settlementId);
    try {
      for (const a of accounts) {
        try {
          const result = await api.post<{ diagnostics?: Diagnostic[]; unresolvedAmount?: number }>(`/finance/settlements/${a.id}/${settlementId}/reconcile`);
          if (result.diagnostics) {
            setDiagnosticsData(prev => ({ ...prev, [settlementId]: {
              diagnostics: result.diagnostics ?? [], totalDiscrepancy: 0, explainedAmount: 0,
              unresolvedAmount: result.unresolvedAmount ?? 0,
            }}));
            setExpandedId(settlementId);
          }
          break;
        } catch { /* try next account */ }
      }
      loadData();
    } catch (err) { setError(err instanceof Error ? err.message : 'Reconciliation failed'); }
    finally { setReconcilingId(null); }
  }

  async function handleExpandDiagnostics(settlementId: string) {
    if (expandedId === settlementId) { setExpandedId(null); return; }
    for (const a of accounts) {
      try {
        const detail = await api.get<{ diagnostics?: DiagnosticInfo }>(`/finance/settlements/${a.id}/${settlementId}`);
        if (detail.diagnostics) setDiagnosticsData(prev => ({ ...prev, [settlementId]: detail.diagnostics ?? null }));
        setExpandedId(settlementId); return;
      } catch { /* try next */ }
    }
    setExpandedId(settlementId);
  }

  // ── Derived data ──

  const filtered = (data?.settlements ?? []).filter(s => {
    if (marketFilter !== 'All') {
      const cur = MARKET_CURRENCIES[marketFilter];
      if (cur && s.originalCurrency !== cur) return false;
    }
    if (statusFilter === 'reconciled' && !s.reconciledAt) return false;
    if (statusFilter === 'unreconciled' && s.reconciledAt) return false;
    if (statusFilter === 'mismatch' && !(s.reconciledAt && (Math.abs(s.revenueDiscrepancy ?? 0) > 1 || Math.abs(s.feeDiscrepancy ?? 0) > 1))) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'desc' ? -1 : 1;
    if (sortBy === 'deposit') return (a.depositDate ?? '').localeCompare(b.depositDate ?? '') * dir;
    if (sortBy === 'amount') return (a.totalAmount - b.totalAmount) * dir;
    if (sortBy === 'period') return (a.settlementStart ?? '').localeCompare(b.settlementStart ?? '') * dir;
    return 0;
  });

  // Available markets from data
  const availableMarkets = [...new Set((data?.settlements ?? []).map(s => CURRENCY_TO_MARKET[s.originalCurrency] ?? s.originalCurrency))].sort();

  // Filtered KPIs
  const filteredKpis = {
    totalDeposited: filtered.reduce((s, r) => s + r.totalAmount, 0),
    count: filtered.length,
    reconciled: filtered.filter(s => s.reconciledAt).length,
    mismatches: filtered.filter(s => s.reconciledAt && (Math.abs(s.revenueDiscrepancy ?? 0) > 1 || Math.abs(s.feeDiscrepancy ?? 0) > 1)).length,
  };

  function toggleSort(field: typeof sortBy) {
    if (sortBy === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(field); setSortDir('desc'); }
  }

  if (loading) return <LoadingSkeleton />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settlements</h1>
          <p className="text-sm text-gray-500 mt-1">Amazon settlement deposits with reconciliation status</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadData} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
            Refresh
          </button>
          <button onClick={handleImport} disabled={importing}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
            {importing ? 'Importing...' : 'Import Settlements'}
          </button>
        </div>
      </div>

      {importMsg && <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">{importMsg}</div>}
      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Market filter */}
        <div className="flex gap-1">
          {['All', ...availableMarkets].map(m => (
            <button key={m} onClick={() => setMarketFilter(m)}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                marketFilter === m ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}>{m}</button>
          ))}
        </div>

        {/* Status filter */}
        <div className="flex gap-1 ml-2">
          {([['all', 'All'], ['reconciled', 'Reconciled'], ['unreconciled', 'Pending'], ['mismatch', 'Mismatches']] as const).map(([val, label]) => (
            <button key={val} onClick={() => setStatusFilter(val)}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                statusFilter === val ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}>{label}</button>
          ))}
        </div>
      </div>

      {/* Page Tabs */}
      <div className="flex gap-1 rounded-lg border border-gray-200 p-1 w-fit">
        {([['settlements', 'Settlements'], ['breakdown', 'P&L Breakdown'], ['transactions', 'Transactions']] as const).map(([id, label]) => (
          <button key={id} onClick={() => { setPageTab(id); if (id === 'transactions') setTxOffset(0); }}
            className={`px-4 py-1.5 text-sm font-medium rounded-md ${pageTab === id ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Date range for P&L tabs */}
      {pageTab !== 'settlements' && (
        <div className="flex items-center gap-3">
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
          <span className="text-sm text-gray-400">to</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
      )}

      {/* ── Settlements Tab ── */}

      {/* KPI Cards */}
      {pageTab === 'settlements' && data && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total Deposited" value={fmtCur(filteredKpis.totalDeposited)} />
          <StatCard label="Settlements" value={`${filteredKpis.count}`} sub={`of ${data.kpis.settlementCount} total`} />
          <StatCard label="Reconciled" value={`${filteredKpis.reconciled} / ${filteredKpis.count}`}
            color={filteredKpis.reconciled === filteredKpis.count ? 'text-green-600' : 'text-yellow-600'} />
          <StatCard label="Mismatches" value={String(filteredKpis.mismatches)}
            color={filteredKpis.mismatches > 0 ? 'text-amber-600' : 'text-green-600'} />
        </div>
      )}

      {/* Empty state */}
      {pageTab === 'settlements' && data && sorted.length === 0 && (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center">
          <p className="text-sm text-gray-500">{data.settlements.length === 0 ? 'No settlements imported yet.' : 'No settlements match current filters.'}</p>
        </div>
      )}

      {/* Settlements Table */}
      {pageTab === 'settlements' && sorted.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Market</th>
                <th className="px-4 py-3 cursor-pointer hover:text-gray-700" onClick={() => toggleSort('period')}>
                  Period {sortBy === 'period' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                </th>
                <th className="px-4 py-3 cursor-pointer hover:text-gray-700" onClick={() => toggleSort('deposit')}>
                  Deposit {sortBy === 'deposit' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                </th>
                <th className="px-4 py-3 text-right cursor-pointer hover:text-gray-700" onClick={() => toggleSort('amount')}>
                  Total {sortBy === 'amount' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                </th>
                <th className="px-4 py-3 text-right">Sales</th>
                <th className="px-4 py-3 text-right">Fees</th>
                <th className="px-4 py-3 text-right">Promos</th>
                <th className="px-4 py-3 text-right">Reimb.</th>
                <th className="px-4 py-3 text-right" title="Reserves, withholdings, other adjustments">Other</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sorted.map((s) => {
                const hasDisc = s.reconciledAt && (Math.abs(s.revenueDiscrepancy ?? 0) > 1 || Math.abs(s.feeDiscrepancy ?? 0) > 1);
                const isExpanded = expandedId === s.settlementId;
                const diag = diagnosticsData[s.settlementId];
                const market = CURRENCY_TO_MARKET[s.originalCurrency] ?? s.originalCurrency;

                return (
                  <Fragment key={s.settlementId}>
                    <tr className={`hover:bg-gray-50 ${hasDisc ? 'cursor-pointer' : ''}`}
                        onClick={hasDisc ? () => handleExpandDiagnostics(s.settlementId) : undefined}>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          market === 'US' ? 'bg-blue-100 text-blue-700' :
                          market === 'CA' ? 'bg-red-100 text-red-700' :
                          market === 'UK' ? 'bg-purple-100 text-purple-700' :
                          market === 'MX' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>{market} <span className="ml-1 font-normal opacity-70">{s.originalCurrency}</span></span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-600">
                        {fmtDate(s.settlementStart)} — {fmtDate(s.settlementEnd)}
                      </td>
                      <td className="px-4 py-2.5 text-xs">{fmtDate(s.depositDate)}</td>
                      <td className="px-4 py-2.5 text-right font-semibold">{fmtCur(s.totalAmount)}</td>
                      <td className="px-4 py-2.5 text-right text-green-600">{fmtCur(s.salesAmount)}</td>
                      <td className="px-4 py-2.5 text-right text-red-600">{fmtCur(s.feeAmount)}</td>
                      <td className="px-4 py-2.5 text-right text-orange-500">{s.promotionAmount ? fmtCur(s.promotionAmount) : '—'}</td>
                      <td className="px-4 py-2.5 text-right text-blue-600">{s.reimbursementAmount ? fmtCur(s.reimbursementAmount) : '—'}</td>
                      <td className="px-4 py-2.5 text-right text-gray-500" title="Reserves, withholdings, adjustments">{s.otherAmount ? fmtCur(s.otherAmount) : '—'}</td>
                      <td className="px-4 py-2.5 text-center">
                        {s.reconciledAt ? (
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            hasDisc ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                          }`} title={hasDisc ? `Rev: ${fmtCur(s.revenueDiscrepancy ?? 0)}, Fees: ${fmtCur(s.feeDiscrepancy ?? 0)}` : `Reconciled ${fmtDate(s.reconciledAt)}`}>
                            {hasDisc ? 'Mismatch' : 'Reconciled'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500">Pending</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {!s.reconciledAt ? (
                          <button onClick={(e) => { e.stopPropagation(); handleReconcile(s.settlementId); }}
                            disabled={reconcilingId === s.settlementId}
                            className="text-xs text-brand-600 hover:text-brand-800 disabled:opacity-50 font-medium">
                            {reconcilingId === s.settlementId ? 'Working...' : 'Reconcile'}
                          </button>
                        ) : hasDisc ? (
                          <span className="text-xs text-gray-400">{isExpanded ? '▲' : '▼'}</span>
                        ) : null}
                      </td>
                    </tr>
                    {isExpanded && hasDisc && (
                      <tr>
                        <td colSpan={11} className="bg-gray-50 px-6 py-4">
                          <DiagnosticPanel diagnostics={diag?.diagnostics ?? []}
                            revenueDisc={s.revenueDiscrepancy ?? 0} feeDisc={s.feeDiscrepancy ?? 0}
                            unresolvedAmount={diag?.unresolvedAmount ?? 0} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── P&L Breakdown Tab ── */}
      {pageTab === 'breakdown' && pnl && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Total Revenue" value={fmtCur(pnl.kpis.totalRevenue)} />
            <StatCard label="Total Fees" value={fmtCur(pnl.kpis.totalFees)} color="text-red-600" />
            <StatCard label="Net Proceeds" value={fmtCur(pnl.kpis.netProceeds)} color={pnl.kpis.netProceeds >= 0 ? 'text-green-600' : 'text-red-600'} />
            <StatCard label="Transactions" value={pnl.kpis.transactionCount.toLocaleString()} />
          </div>

          {/* Category Breakdown */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="text-sm font-semibold mb-3">Category Breakdown</h3>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-100">
                <th className="pb-2 text-left text-xs font-medium text-gray-500">Category</th>
                <th className="pb-2 text-right text-xs font-medium text-gray-500">Amount</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50">
                {[
                  ['Product Sales', pnl.kpis.productSales],
                  ['Shipping Credits', pnl.kpis.shippingCredits],
                  ['Gift Wrap Credits', pnl.kpis.giftWrapCredits],
                  ['Product Sales Tax', pnl.kpis.productSalesTax],
                  ['Promotional Rebates', pnl.kpis.promotionalRebates],
                  ['Selling Fees', pnl.kpis.sellingFees],
                  ['FBA Fees', pnl.kpis.fbaFees],
                  ['Other Fees', pnl.kpis.otherFees],
                  ['Marketplace Withheld Tax', pnl.kpis.marketplaceWithheldTax],
                ].filter(([, v]) => v !== 0).map(([label, amount]) => (
                  <tr key={label as string}>
                    <td className="py-2 text-gray-700">{label}</td>
                    <td className={`py-2 text-right font-medium ${(amount as number) >= 0 ? 'text-gray-900' : 'text-red-600'}`}>{fmtCur(amount as number)}</td>
                  </tr>
                ))}
                <tr className="font-semibold border-t border-gray-200">
                  <td className="py-2 text-gray-900">Net Proceeds</td>
                  <td className={`py-2 text-right ${pnl.kpis.netProceeds >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtCur(pnl.kpis.netProceeds)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Daily Breakdown */}
          {pnl.daily.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <h3 className="text-sm font-semibold mb-3">Daily Breakdown</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-100">
                    <th className="pb-2 text-left text-xs font-medium text-gray-500">Date</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">Product Sales</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">Selling Fees</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">FBA Fees</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">Promos</th>
                    <th className="pb-2 text-right text-xs font-medium text-gray-500">Total</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {pnl.daily.map(d => (
                      <tr key={d.date}>
                        <td className="py-2 text-gray-700">{d.date}</td>
                        <td className="py-2 text-right font-medium">{fmtCur(d.product_sales)}</td>
                        <td className="py-2 text-right text-red-600">{fmtCur(d.selling_fees)}</td>
                        <td className="py-2 text-right text-red-600">{fmtCur(d.fba_fees)}</td>
                        <td className="py-2 text-right text-gray-500">{fmtCur(d.promotional_rebates)}</td>
                        <td className={`py-2 text-right font-medium ${d.total >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtCur(d.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
      {pageTab === 'breakdown' && !pnl && !loading && (
        <p className="text-sm text-gray-400 text-center py-8">No settlement P&L data for this date range.</p>
      )}

      {/* ── Transactions Tab ── */}
      {pageTab === 'transactions' && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold">Transactions ({txTotal.toLocaleString()} total)</h3>
            {txTotal > 100 && (
              <div className="flex items-center gap-2">
                <button disabled={txOffset === 0} onClick={() => setTxOffset(Math.max(0, txOffset - 100))}
                  className="rounded border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50">Prev</button>
                <span className="text-xs text-gray-500">{txOffset + 1}–{Math.min(txOffset + 100, txTotal)}</span>
                <button disabled={txOffset + 100 >= txTotal} onClick={() => setTxOffset(txOffset + 100)}
                  className="rounded border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50">Next</button>
              </div>
            )}
          </div>
          {txLoading ? (
            <p className="py-8 text-center text-sm text-gray-400">Loading transactions...</p>
          ) : transactions.length === 0 ? (
            <p className="text-sm text-gray-400">No transactions for this date range.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-100">
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Order ID</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">SKU</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Type</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Description</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Amount</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {transactions.map((tx, i) => (
                    <tr key={`${tx.orderId}-${tx.amountType}-${i}`}>
                      <td className="py-2 max-w-[140px] truncate text-gray-700" title={tx.orderId}>{tx.orderId || '—'}</td>
                      <td className="py-2 max-w-[150px] text-gray-700" title={tx.sku}>
                        {tx.sku ? (<>
                          {parentNickname(tx.sku) && <span className="text-[10px] text-gray-400 block">{parentNickname(tx.sku)}</span>}
                          {displayName(tx.sku)}
                        </>) : '—'}
                      </td>
                      <td className="py-2 text-gray-500 text-xs">{tx.amountType}</td>
                      <td className="py-2 text-gray-500 text-xs">{tx.amountDescription}</td>
                      <td className={`py-2 text-right font-medium ${tx.amount >= 0 ? 'text-gray-900' : 'text-red-600'}`}>{fmtCur(tx.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className={`mt-1 text-xl font-semibold ${color ?? 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function DiagnosticPanel({ diagnostics, revenueDisc, feeDisc, unresolvedAmount }: {
  diagnostics: Diagnostic[]; revenueDisc: number; feeDisc: number; unresolvedAmount: number;
}) {
  const totalDisc = Math.abs(revenueDisc) + Math.abs(feeDisc);
  const labels: Record<string, string> = { timing: 'Timing (Settlement Boundary)', refund_lag: 'Refund Lag', fba_adjustments: 'FBA Adjustments', fee_corrections: 'Fee Corrections / Other' };
  const colors: Record<string, string> = { timing: 'bg-green-50 border-green-200', refund_lag: 'bg-yellow-50 border-yellow-200', fba_adjustments: 'bg-yellow-50 border-yellow-200', fee_corrections: 'bg-red-50 border-red-200' };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 text-xs">
        <span className="font-semibold text-gray-700">Discrepancy: {fmtCur(totalDisc)}</span>
        <span className="text-gray-500">Revenue: {fmtCur(revenueDisc)}</span>
        <span className="text-gray-500">Fees: {fmtCur(feeDisc)}</span>
      </div>
      {diagnostics.length === 0 ? (
        <p className="text-xs text-gray-400">No diagnostic data. Re-reconcile to generate.</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {diagnostics.map((d, i) => (
            <div key={i} className={`rounded-lg border p-3 ${colors[d.reason] ?? 'bg-gray-50 border-gray-200'}`}>
              <div className="flex justify-between text-xs font-medium"><span>{labels[d.reason] ?? d.reason}</span><span>{fmtCur(Math.abs(d.amount))}</span></div>
              <p className="mt-1 text-[11px] text-gray-500">{d.explanation}</p>
            </div>
          ))}
        </div>
      )}
      {unresolvedAmount > 1 && <p className="text-xs text-amber-600">Unresolved: {fmtCur(unresolvedAmount)}</p>}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 w-48 rounded bg-gray-200" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-20 rounded-xl bg-gray-200" />)}
      </div>
      <div className="h-64 rounded-xl bg-gray-200" />
    </div>
  );
}

function fmtCur(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}
